import React, { useCallback, useEffect, useState } from 'react';
import { pipelineFormStyles as formStyles } from 'pages/pipelines/CreateOrUpdatePipeline/components/pipeline-form-helper';
import { Box, DialogActions, DialogContent } from '@mui/material';
import { Controller } from 'react-hook-form';
import {
    Button,
    Dialog,
    DragAndDrop,
    ErrorMessage,
    FormLabel,
    TextField as MuiTextField,
} from '@biq/apex-ui';
import CodeMirror from '@uiw/react-codemirror';
import { CodeMirrorStyles, validationsMessages } from 'lib';
import {
    commonStyles,
    PipelinesCodeMirrorStyles,
} from 'pages/pipelines/pipelines-helper';
import { JobFieldHeadingWithTooltip } from 'pages/job-manager';
import { CustomIconButton } from '../../../../components';
import {
    JavascriptTransformFormProps,
    javascriptTransformFormStyles,
} from './javascript-transform-form-helper';
import {
    FileData,
    replaceModalConfig,
} from 'pages/job-manager/components/create-job-dialog';
import { useLocation } from 'react-router-dom';

export const JavascriptTransformForm = ({
    control,
    errors,
    watch,
    setValue,
    clearErrors,
    setError,
}: JavascriptTransformFormProps): React.JSX.Element => {
    const location = useLocation();
    const [openDragAndDropModal, setOpenDragAndDropModal] = useState(false);
    const [files, setFiles] = useState<FileData[]>([]);
    const [prevFiles, setPrevFiles] = useState<FileData[]>([]);
    const [fileError, setFileError] = useState<boolean>(false);
    const [fileTextContent, setFileTextContent] = useState<string>('');
    const jsBase64 = watch('jsBase64');
    const editorHeight = location.pathname.includes('steps/edit')
        ? '264px'
        : '377px';

    const onTransformValueChange = useCallback((value: string) => {
        setValue('jsBase64', value);
        if (value) {
            clearErrors('jsBase64');
        } else {
            setError('jsBase64', {
                type: 'required',
                message: validationsMessages.required,
            });
        }
    }, []);

    const handleRefreshButtonClick = (): void => {
        onTransformValueChange('');
        setFiles([]);
        setPrevFiles([]);
    };

    const handleUploadButtonClick = (): void => {
        setOpenDragAndDropModal(true);
    };

    useEffect(() => {
        if (!files?.[0]?.url) return;
        setFileError(false);
        fetch(files[0].url)
            .then((res) => res.blob())
            .then((blob) => blob.text())
            .then((textContent) => {
                setFileTextContent(textContent);
            })
            .catch((err) => console.error('Failed to read blob', err));
    }, [files]);

    const onApplyButtonClick = (): void => {
        if (files.length === 0) {
            setFileError(true);
            return;
        }
        setPrevFiles(files);
        setOpenDragAndDropModal(false);
        onTransformValueChange(fileTextContent);
    };

    const onClickDragAndDropModalClose = (): void => {
        setOpenDragAndDropModal(false);
        setFileError(false);
        setFiles(prevFiles);
    };

    return (
        <>
            <Box sx={formStyles.mainContainer}>
                <Box sx={formStyles.fieldGroup}>
                    <Box>
                        <JobFieldHeadingWithTooltip
                            label='Entry Function Name'
                            title={
                                'If left empty, the function name will default to ‘xform’.'
                            }
                            required
                            popperStyles={
                                javascriptTransformFormStyles.popperStyles
                            }
                            modifiers={[
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, 4],
                                    },
                                },
                            ]}
                        />
                    </Box>
                    <Controller
                        name='funcName'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <MuiTextField
                                {...field}
                                variant='outlined'
                                value={field.value}
                                onChange={(event): void => {
                                    field.onChange(event);
                                }}
                                onBlur={(value): void => {
                                    field.onChange(value.target.value.trim());
                                }}
                                required
                                error={!!errors?.funcName}
                                errorText={
                                    (errors?.funcName?.message as string) ?? ''
                                }
                            />
                        )}
                    />
                </Box>
                <Box sx={formStyles.fieldGroup}>
                    <Box
                        sx={
                            javascriptTransformFormStyles.transformLabelContainer
                        }
                    >
                        <FormLabel required label='Transform' />
                        <Box
                            sx={javascriptTransformFormStyles.actionsContainer}
                        >
                            <CustomIconButton
                                title='Clear JSON'
                                className='icon-button'
                                icon='mdi:refresh'
                                onClick={handleRefreshButtonClick}
                            />
                            <CustomIconButton
                                title='Upload'
                                className='icon-button'
                                icon='mdi:upload'
                                onClick={handleUploadButtonClick}
                            />
                        </Box>
                    </Box>
                    <Controller
                        name='jsBase64'
                        control={control}
                        render={(): React.JSX.Element => (
                            <>
                                <CodeMirror
                                    value={jsBase64}
                                    style={{
                                        ...commonStyles.codeMirrorConatiner,
                                        ...(errors.jsBase64
                                            ? commonStyles.codeMirrorError
                                            : {}),
                                    }}
                                    height={editorHeight}
                                    extensions={[
                                        CodeMirrorStyles,
                                        PipelinesCodeMirrorStyles,
                                    ]}
                                    onChange={onTransformValueChange}
                                />
                                {errors?.jsBase64 && (
                                    <Box pt={0.25}>
                                        <ErrorMessage
                                            errorMessage={
                                                validationsMessages.required
                                            }
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    />
                </Box>
            </Box>
            <Dialog
                sx={javascriptTransformFormStyles.dragAndDropDialogStyles}
                open={openDragAndDropModal}
                closable={true}
                onClose={onClickDragAndDropModalClose}
                title='Upload File'
            >
                <DialogContent>
                    <DragAndDrop
                        files={files}
                        setFiles={setFiles}
                        fileError={fileError}
                        setFileError={setFileError}
                        filePreview={true}
                        showReplaceFileModal={true}
                        replaceModalConfig={replaceModalConfig}
                        acceptedFileTypes={{
                            'text/javascript': ['.js'],
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        autoFocus
                        onClick={onApplyButtonClick}
                        color='primary'
                        variant='contained'
                        size='small'
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
